import { Component } from 'react';
import { connect } from 'react-redux';

import get from 'lodash/get';
import toLower from 'lodash/toLower';
import PropTypes from 'prop-types';

import { Box, Button, Typography } from '@mui/material';

import i18n from 'shared/lib/i18n';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';
import CallCenterRouterComponent from './CallCenterRouterComponent';

import {
  CALL_CENTER_NO_ACTION,
  SET_CALL_CENTER_MODAL_TYPE,
} from '../../actionConstants';
import { openDoor } from '../AccessPoint/actionCreators';
import { exchangeToken, logout } from '../Auth/actionCreators';
import { showRenderPropsModal } from '../Modal/actions';

import { isAdmin, isAuthenticated, isService } from '../../utils';
import {
  createGeneralDelivery,
  loadCallCenter,
  openAccessPointForEmergency,
  openAccessPointForPerson,
  openAccessPointForVisitor,
  setCurrentCameraId,
} from './utils';

import apiClient from '../../apiClient';
import { history } from '../../store';

const getIsLeasingShown = (props) => {
  const {
    location: { search },
  } = props;
  const urlParams = new URLSearchParams(toLower(search));
  const callReason = urlParams.get('call_reason');
  return callReason === 'leasing_forward';
};

const getModalProps = (remoteDoormanPopup) => ({
  maxWidth: 'sm',
  isCloseOnBackdropClick: false,
  render: (props) => (
    <Box>
      <Box display="flex" flexDirection="column" gap="1rem">
        <Box flexGrow={1}>
          <Typography variant="h5">{remoteDoormanPopup?.headline}</Typography>
          <Typography variant="body">{remoteDoormanPopup?.body}</Typography>
        </Box>
        <Box maxHeight="20rem">
          {!!remoteDoormanPopup?.photo && (
            <ExpandableImageComponent
              photo={remoteDoormanPopup?.photo}
              caption={remoteDoormanPopup?.headline}
            />
          )}
        </Box>
      </Box>
      <Box width="50%" m="auto" mt="2rem">
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={props.onModalClose}
          fullWidth
        >
          {i18n.t('callCenter.confirmRead')}
        </Button>
      </Box>
    </Box>
  ),
});
class CallCenterRouterContainer extends Component {
  constructor(props) {
    super(props);
    this.phoneNumber = null;
    this.state = {
      isEmergencyServicesShown: false,
      isLeasingShown: getIsLeasingShown(props),
    };
  }

  async componentDidMount() {
    const {
      location: { search },
      match: { params },
      onLoad,
      user,
    } = this.props;
    const urlParams = new URLSearchParams(toLower(search));
    this.phoneNumber = params.phoneNumber || urlParams.get('phone_number');
    const res = await onLoad(urlParams.get('token'), this.phoneNumber, user);
    const hasRemoteDoormanPopup = Boolean(
      res?.data?.building?.remoteDoormanPopup?.id,
    );
    hasRemoteDoormanPopup &&
      this.showRemoteDoormanPopup(res.data.building.remoteDoormanPopup);
  }

  setIsEmergencyServicesShown = (isEmergencyServicesShown) =>
    this.setState({ isEmergencyServicesShown });

  setIsLeasingShown = (isLeasingShown) => this.setState({ isLeasingShown });

  showRemoteDoormanPopup = (remoteDoormanPopup) => {
    const { showRenderPropsModal } = this.props;
    const modalProps = getModalProps(remoteDoormanPopup);
    showRenderPropsModal(modalProps);
  };

  render() {
    const { isEmergencyServicesShown, isLeasingShown } = this.state;
    const props = {
      ...this.props,
      isEmergencyServicesShown,
      setIsEmergencyServicesShown: this.setIsEmergencyServicesShown,
      isLeasingShown,
      setIsLeasingShown: this.setIsLeasingShown,
      phoneNumber: this.phoneNumber,
      showRemoteDoormanPopup: this.showRemoteDoormanPopup,
    };
    return <CallCenterRouterComponent {...props} />;
  }
}

CallCenterRouterContainer.propTypes = {
  location: PropTypes.instanceOf(Object).isRequired,
  match: PropTypes.instanceOf(Object).isRequired,
  user: PropTypes.instanceOf(Object).isRequired,
  onLoad: PropTypes.func.isRequired,
  showRenderPropsModal: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  ...state.callCenter,
  isLoading: !!state.common.loading.length,
  user: get(state, 'common.currentUser', null),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  onLoad: async (token, phoneNumber) => {
    const { location } = ownProps;
    if (isAdmin() || isService()) {
      return loadCallCenter(dispatch, phoneNumber);
    }
    if (token && phoneNumber) {
      const jwtToken = await exchangeToken(dispatch, { token });
      return jwtToken
        ? loadCallCenter(dispatch, phoneNumber)
        : history.replace('/login', { from: location });
    }
    if (isAuthenticated()) {
      return history.replace('/');
    }
    return history.replace('/login', { from: location });
  },
  openAccessPointForPerson: (accessPoint, person) => {
    openAccessPointForPerson(dispatch, accessPoint, person);
  },
  openAccessPointForVisitor: (accessPoint, visitor) => {
    openAccessPointForVisitor(dispatch, accessPoint, visitor);
  },
  openAccessPointForEmergency: (accessPoint, data) => {
    openAccessPointForEmergency(dispatch, accessPoint, data);
  },
  createGeneralDelivery: (accessPoint, doors) => {
    createGeneralDelivery(dispatch, accessPoint, doors);
  },
  openDoor: (door) => openDoor(dispatch, door),
  setCallCenterModalType: (modalType, defaultDeliveryType = null) =>
    dispatch({
      type: SET_CALL_CENTER_MODAL_TYPE,
      modalType,
      defaultDeliveryType,
    }),
  noAction: (props) => {
    dispatch({
      type: CALL_CENTER_NO_ACTION,
      payload: apiClient.callCenter.noAction(get(props, 'building.id')),
    });
  },
  setCurrentCameraId: (id) => setCurrentCameraId(dispatch, id),
  logout: () => logout(dispatch),
  showRenderPropsModal: (modalProps) =>
    showRenderPropsModal(dispatch, modalProps),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CallCenterRouterContainer);
